import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTenant } from '@/contexts'
import {
  fetchTransientStays,
  fetchTransientStayById,
  createTransientStay,
  updateTransientStay,
  deleteTransientStay,
  cancelTransientStay,
  checkOutTransientStay,
  noShowTransientStay,
  terminateTransientStay,
} from '@/lib/api/transient-stays'
import { broadcastInvalidation } from '@/lib/sync'
import type { TransientStayFormData, TransientStayFilters } from '@/types/transient-stays'
import { toast } from 'sonner'

/**
 * Query key factory for transient stays
 * Provides consistent keys for cache management
 */
export const transientStayKeys = {
  all: ['transient-stays'] as const,
  lists: () => [...transientStayKeys.all, 'list'] as const,
  list: (organizationId: string, filters?: TransientStayFilters) =>
    [...transientStayKeys.lists(), organizationId, filters] as const,
  details: () => [...transientStayKeys.all, 'detail'] as const,
  detail: (id: string) => [...transientStayKeys.details(), id] as const,
}

/**
 * Fetch all transient stays for current organization with optional filtering
 */
export function useTransientStays(filters?: TransientStayFilters) {
  const { currentOrganization } = useTenant()
  const organizationId = currentOrganization?.id

  return useQuery({
    queryKey: transientStayKeys.list(organizationId || '', filters),
    queryFn: () => fetchTransientStays(organizationId!, filters),
    enabled: !!organizationId,
    refetchOnWindowFocus: true,
  })
}

/**
 * Fetch a single transient stay by ID
 */
export function useTransientStayById(id: string | undefined) {
  return useQuery({
    queryKey: transientStayKeys.detail(id || ''),
    queryFn: () => fetchTransientStayById(id!),
    enabled: !!id,
  })
}

/**
 * Create a new transient stay
 */
export function useCreateTransientStay() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useTenant()

  return useMutation({
    mutationFn: async (data: TransientStayFormData) => {
      return createTransientStay(currentOrganization!.id, data)
    },
    onSuccess: () => {
      const keys = [transientStayKeys.lists(), ['map-allocations'], ['allocations']] as const
      keys.forEach((k) => queryClient.invalidateQueries({ queryKey: [...k] }))
      broadcastInvalidation(keys)
      toast.success('Transient stay created successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to create transient stay', {
        description: error.message,
      })
    },
  })
}

/**
 * Update an existing transient stay
 */
export function useUpdateTransientStay() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: Partial<TransientStayFormData>
    }) => {
      return updateTransientStay(id, data)
    },
    onSuccess: (_, { id }) => {
      const keys = [transientStayKeys.lists(), transientStayKeys.detail(id), ['map-allocations'], ['allocations']] as const
      keys.forEach((k) => queryClient.invalidateQueries({ queryKey: [...k] }))
      broadcastInvalidation(keys)
      toast.success('Transient stay updated successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to update transient stay', {
        description: error.message,
      })
    },
  })
}

/**
 * Delete a transient stay (soft delete)
 */
export function useDeleteTransientStay() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => deleteTransientStay(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: transientStayKeys.lists() })
      broadcastInvalidation([transientStayKeys.lists()])
      toast.success('Transient stay deleted successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to delete transient stay', {
        description: error.message,
      })
    },
  })
}

/**
 * Cancel a transient stay (from inquiry/reserved/confirmed)
 */
export function useCancelTransientStay() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, cancellation_reason }: { id: string; cancellation_reason?: string }) =>
      cancelTransientStay(id, cancellation_reason ? { cancellation_reason } : undefined),
    onSuccess: (_, { id }) => {
      const keys = [transientStayKeys.lists(), transientStayKeys.detail(id), ['map-allocations'], ['allocations']] as const
      keys.forEach((k) => queryClient.invalidateQueries({ queryKey: [...k] }))
      broadcastInvalidation(keys)
      toast.success('Transient stay cancelled')
    },
    onError: (error: Error) => {
      toast.error('Failed to cancel transient stay', {
        description: error.message,
      })
    },
  })
}

/**
 * Check out a transient stay (from checked_in)
 */
export function useCheckOutTransientStay() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => checkOutTransientStay(id),
    onSuccess: (_, id) => {
      const keys = [transientStayKeys.lists(), transientStayKeys.detail(id), ['map-allocations'], ['allocations']] as const
      keys.forEach((k) => queryClient.invalidateQueries({ queryKey: [...k] }))
      broadcastInvalidation(keys)
      toast.success('Transient stay checked out')
    },
    onError: (error: Error) => {
      toast.error('Failed to check out transient stay', {
        description: error.message,
      })
    },
  })
}

/**
 * Mark a transient stay as no-show (from reserved/confirmed)
 */
export function useNoShowTransientStay() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => noShowTransientStay(id),
    onSuccess: (_, id) => {
      const keys = [transientStayKeys.lists(), transientStayKeys.detail(id), ['map-allocations'], ['allocations']] as const
      keys.forEach((k) => queryClient.invalidateQueries({ queryKey: [...k] }))
      broadcastInvalidation(keys)
      toast.success('Marked as no-show')
    },
    onError: (error: Error) => {
      toast.error('Failed to mark as no-show', {
        description: error.message,
      })
    },
  })
}

/**
 * Terminate a transient stay early (from checked_in)
 */
export function useTerminateTransientStay() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, termination_reason }: { id: string; termination_reason?: string }) =>
      terminateTransientStay(id, termination_reason ? { termination_reason } : undefined),
    onSuccess: (_, { id }) => {
      const keys = [transientStayKeys.lists(), transientStayKeys.detail(id), ['map-allocations'], ['allocations']] as const
      keys.forEach((k) => queryClient.invalidateQueries({ queryKey: [...k] }))
      broadcastInvalidation(keys)
      toast.success('Transient stay terminated')
    },
    onError: (error: Error) => {
      toast.error('Failed to terminate transient stay', {
        description: error.message,
      })
    },
  })
}
